import { Button, Icon, Label, Tooltip, token } from "@may-i/mash-design-system";
import { type ChangeEvent, memo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import styled, { css } from "styled-components";
import type { Camera } from "~/entities/camera/types";
import { isCameraAnalyzed } from "~/entities/camera/utils";
import { IcDelete } from "~/shared/assets/icons/IcDelete";
import { TEMP_IMAGE_URL } from "~/shared/ui/EmptyImagePlaceholder";
import { ImageLightbox } from "./ImageLightbox";

interface CameraCardProps {
  camera: Camera;
  displayName?: string;
  selecting?: boolean;
  selected?: boolean;
  isSaving?: boolean;
  isVersionInactive?: boolean;
  onToggleSelect?: (id: number) => void;
  onImageSave: (id: number, data: { imageFile?: File }) => void;
  onDelete: (cameraOrientationId: number, name: string) => void;
}

export const CameraCard = memo(function CameraCard({ camera, displayName, selecting, selected, isSaving, isVersionInactive, onToggleSelect, onImageSave, onDelete }: CameraCardProps) {
  const { t } = useTranslation("resource");
  const [editing, setEditing] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | undefined>(undefined);
  const [imageFile, setImageFile] = useState<File | undefined>(undefined);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleStartEdit = () => {
    setPreviewUrl(undefined);
    setImageFile(undefined);
    setEditing(true);
    requestAnimationFrame(() => fileInputRef.current?.click());
  };

  const handleCancel = () => {
    setEditing(false);
  };

  const handleSave = () => {
    onImageSave(camera.id, { imageFile });
    setEditing(false);
  };

  const handleImageChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const displayImageUrl = previewUrl ?? camera.previewImageUrl ?? camera.originalImageUrl;
  const isAnalyzed = isCameraAnalyzed(camera.analysisNumber);

  if (selecting) {
    // 분석 중 카메라는 일괄 삭제 모드에서도 선택 불가
    const card = (
      <Card
        $selected={selected}
        $dimmed={isAnalyzed}
        onClick={isAnalyzed ? undefined : () => onToggleSelect?.(camera.id)}
        style={{ cursor: isAnalyzed ? "not-allowed" : "pointer" }}
      >
        <ImageArea>
          <CardImage src={displayImageUrl || TEMP_IMAGE_URL} alt={camera.defaultCamId} />
          {selected && <SelectOverlay />}
          {!isAnalyzed && (
            <SelectCheckbox>
              {selected ? <CheckOnIcon /> : <CheckOffIcon />}
            </SelectCheckbox>
          )}
        </ImageArea>
        <InfoRow>
          <InfoLeft>
            <Label
              variant={isAnalyzed ? "soft" : "filled"}
              color={isAnalyzed ? "success" : "default"}
              size="xSmall"
            >
              {isAnalyzed ? t("status.analyzed") : t("status.unanalyzed")}
            </Label>
            <CardName>{displayName ?? camera.defaultCamId}</CardName>
          </InfoLeft>
        </InfoRow>
      </Card>
    );
    if (isAnalyzed) {
      return (
        <Tooltip trigger={card} side="top" sideOffset={4}>
          {t("tooltip.cannotDeleteAnalyzedCamera")}
        </Tooltip>
      );
    }
    return card;
  }

  if (editing) {
    return (
      <Card $editing>
        <ImageArea>
          <CardImage src={displayImageUrl || TEMP_IMAGE_URL} alt={camera.defaultCamId} />
          <ReplaceButton onClick={() => fileInputRef.current?.click()}>
            {t("action.replaceImage")}
          </ReplaceButton>
          <HiddenInput
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleImageChange}
          />
        </ImageArea>
        <InfoRow $editing>
          <InfoLeft>
            <Label
              variant={isAnalyzed ? "soft" : "filled"}
              color={isAnalyzed ? "success" : "default"}
              size="xSmall"
            >
              {isAnalyzed ? t("status.analyzed") : t("status.unanalyzed")}
            </Label>
            <CardName>{displayName ?? camera.defaultCamId}</CardName>
          </InfoLeft>
          <InfoRight>
            <TextButton onClick={handleCancel} disabled={isSaving}>{t("action.cancel")}</TextButton>
            <TextButton onClick={handleSave} disabled={isSaving}>{t("action.save")}</TextButton>
          </InfoRight>
        </InfoRow>
      </Card>
    );
  }

  return (
    <Card>
      <ImageArea>
        <CardImage src={displayImageUrl || TEMP_IMAGE_URL} alt={camera.defaultCamId} />
        {/* 분석 중 카메라는 이미지 교체 불가 — 단, 비활성 버전에서는 허용 */}
        {(!isAnalyzed || isVersionInactive) && (
          <HoverReplaceButton onClick={handleStartEdit}>
            {t("action.replaceImage")}
          </HoverReplaceButton>
        )}
        <ViewFullButton onClick={() => setLightboxOpen(true)}>
          <Icon icon="ic_fullsize2" width={16} height={16} />
        </ViewFullButton>
      </ImageArea>
      <ImageLightbox
        open={lightboxOpen}
        src={displayImageUrl || TEMP_IMAGE_URL}
        alt={camera.defaultCamId}
        onClose={() => setLightboxOpen(false)}
      />
      <InfoRow>
        <InfoLeft>
          <Label
            variant={isAnalyzed ? "soft" : "filled"}
            color={isAnalyzed ? "success" : "default"}
            size="xSmall"
          >
            {isAnalyzed ? t("status.analyzed") : t("status.unanalyzed")}
          </Label>
          <CardName>{displayName ?? camera.defaultCamId}</CardName>
        </InfoLeft>
        <InfoRight>
          {isAnalyzed ? (
            <Tooltip
              trigger={
                <IconButton disabled aria-disabled="true">
                  <IcDelete width={16} height={16} />
                </IconButton>
              }
              side="top"
              sideOffset={4}
            >
              {t("tooltip.cannotDeleteAnalyzedCamera")}
            </Tooltip>
          ) : (
            <IconButton onClick={() => onDelete(camera.id, displayName ?? camera.defaultCamId)}>
              <IcDelete width={16} height={16} />
            </IconButton>
          )}
        </InfoRight>
      </InfoRow>
    </Card>
  );
});

// --- New Camera Card ---

interface NewCameraCardProps {
  isSaving?: boolean;
  onSave: (data: { imageFile?: File }) => void;
  onCancel: () => void;
}

export function NewCameraCard({ isSaving, onSave, onCancel }: NewCameraCardProps) {
  const { t } = useTranslation("resource");
  const [previewUrl, setPreviewUrl] = useState<string | undefined>(undefined);
  const [imageFile, setImageFile] = useState<File | undefined>(undefined);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleImageChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith("image/")) {
      setImageFile(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const handleSave = () => {
    onSave({ imageFile });
  };

  return (
    <Card $editing>
      <ImageArea
        $clickable={!previewUrl}
        onClick={() => !previewUrl && fileInputRef.current?.click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={handleDrop}
      >
        {previewUrl ? (
          <CardImage src={previewUrl} alt="" />
        ) : (
          <EmptyEditImageArea>
            <Button variant="outlined" color="primary" size="xSmall">
              {t("action.addImage")}
            </Button>
          </EmptyEditImageArea>
        )}
        <HiddenInput
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleImageChange}
        />
      </ImageArea>
      <InfoRow>
        <InfoLeft>
          <Label variant="filled" color="default" size="xSmall">
            {t("status.unanalyzed")}
          </Label>
        </InfoLeft>
        <InfoRight>
          <TextButton onClick={onCancel} disabled={isSaving}>{t("action.cancel")}</TextButton>
          <TextButton onClick={handleSave} disabled={!imageFile || isSaving}>
            {t("action.save")}
          </TextButton>
        </InfoRight>
      </InfoRow>
    </Card>
  );
}

// --- Styles ---

const Card = styled.div<{ $editing?: boolean; $selected?: boolean; $dimmed?: boolean }>`
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  border: 1px solid ${({ $editing, $selected }) =>
    $editing ? "#484BD2" : $selected ? "#242946" : token("colors.border.outline")};
  box-shadow: ${({ $editing, $selected }) =>
    $editing ? "0 0 0 1px #484BD2" : $selected ? "0 0 0 1px #242946" : "none"};
  background: #fff;
  overflow: hidden;
  ${({ $dimmed }) =>
    $dimmed &&
    css`
      opacity: 0.5;
    `}
`;

const ImageArea = styled.div<{ $clickable?: boolean }>`
  position: relative;
  aspect-ratio: 299 / 168;
  border-bottom: 1px solid ${token("colors.border.outline")};
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;

  ${({ $clickable }) =>
    $clickable &&
    css`
      cursor: pointer;
    `}
`;

const CardImage = styled.img`
  display: block;
  width: 100%;
  height: 100%;
  /* 도면 카드와 동일하게 원본 비율 보존 — cover 는 비-16:9 스냅샷을 잘라냈다.
     빈 카메라의 placeholder(temp_image.png) 는 16:9 라 보이는 결과가 그대로다 */
  object-fit: contain;
`;

const EmptyEditImageArea = styled.div`
  width: 100%;
  height: 100%;
  background: ${token("colors.gray.GR50")};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ViewFullButton = styled.button`
  position: absolute;
  bottom: 8px;
  right: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border-radius: 3px;
  border: none;
  background: rgba(17, 17, 31, 0.32);
  color: #fff;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.2s;

  ${Card}:hover & {
    opacity: 1;
  }
`;

const ReplaceButton = styled.button`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  padding: 3px 8px;
  border-radius: 6px;
  border: none;
  background: rgba(17, 17, 31, 0.32);
  color: #fff;
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.5;
  cursor: pointer;
  box-shadow: 0px 20px 40px -4px rgba(105, 111, 140, 0.16);
`;

/** Default 모드에서 카드 hover 시 노출되는 이미지 교체 버튼 */
const HoverReplaceButton = styled.button`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  padding: 3px 8px;
  border-radius: 6px;
  border: none;
  background: rgba(17, 17, 31, 0.32);
  color: #fff;
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.5;
  cursor: pointer;
  box-shadow: 0px 20px 40px -4px rgba(105, 111, 140, 0.16);
  opacity: 0;
  transition: opacity 0.2s;

  ${Card}:hover & {
    opacity: 1;
  }
`;

const InfoRow = styled.div<{ $editing?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 36px;
  padding: 0 8px;
  gap: ${({ $editing }) => ($editing ? "8px" : "12px")};
`;

const InfoLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex: 1;
  min-width: 0;
`;

const InfoRight = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
`;

const CardName = styled.span`
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.5;
  color: ${token("colors.text.primary")};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const IconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  color: ${token("colors.text.primary")};

  &:hover {
    color: ${token("colors.text.secondary")};
  }

  &:disabled {
    color: rgba(143, 149, 178, 0.5);
    cursor: not-allowed;
    &:hover {
      color: rgba(143, 149, 178, 0.5);
    }
  }
`;

const TextButton = styled.button`
  padding: 3px 6px;
  border: none;
  border-radius: 6px;
  background: transparent;
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.5;
  color: ${token("colors.text.primary")};
  cursor: pointer;

  &:hover {
    background: ${token("colors.action.hover")};
  }

  &:disabled {
    color: rgba(143, 149, 178, 0.8);
    cursor: default;

    &:hover {
      background: transparent;
    }
  }
`;

const HiddenInput = styled.input`
  display: none;
`;

const SelectOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.24);
  pointer-events: none;
`;

const SelectCheckbox = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  padding: 6px;
  display: flex;
  line-height: 0;
`;

function CheckOnIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path
        d="M12.0005 0.833328C13.7492 0.833504 15.1665 2.25153 15.1665 4.00032V12.0003C15.1663 13.749 13.7491 15.1662 12.0005 15.1663H4.00049C2.2517 15.1663 0.833672 13.7491 0.833496 12.0003V4.00032C0.833496 2.25142 2.25159 0.833328 4.00049 0.833328H12.0005ZM11.1079 5.88216C11.0428 5.81726 10.9376 5.81712 10.8726 5.88216L7.57275 9.18196C7.37759 9.37708 7.061 9.37688 6.86572 9.18196L5.45166 7.7679C5.38658 7.70285 5.28041 7.70285 5.21533 7.7679C5.15075 7.83283 5.15052 7.93815 5.21533 8.00325L7.10107 9.88899C7.13232 9.92024 7.17506 9.93781 7.21924 9.93782C7.26283 9.93773 7.30499 9.92031 7.33643 9.88899L11.1079 6.11751C11.173 6.05242 11.173 5.94724 11.1079 5.88216Z"
        fill="#242946"
        stroke="white"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function CheckOffIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <rect x="1.333" y="1.333" width="13.334" height="13.334" rx="3.167" stroke="rgba(143,149,178,0.48)" strokeWidth="1" fill="white" />
    </svg>
  );
}
